import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type WorkflowCardFieldDisplayOrderKind = 'native' | 'custom';

export interface WorkflowCardFieldDisplayOrderItem {
  kind: WorkflowCardFieldDisplayOrderKind;
  isTitleLocked: boolean;
  nativeCardFieldKey: string | null;
  workflowFieldId: string | null;
  label: string;
}

export interface WorkflowCardFieldDisplayOrderPutItem {
  kind: WorkflowCardFieldDisplayOrderKind;
  nativeCardFieldKey: string | null;
  workflowFieldId: string | null;
}

@Injectable({ providedIn: 'root' })
export class WorkflowCardFieldDisplayOrderApiService {
  private readonly http = inject(HttpClient);
  private readonly cache = new Map<string, { items: WorkflowCardFieldDisplayOrderItem[]; at: number }>();
  private readonly ttlMs = 60_000;

  /** Limpa cache (ex.: após alterações externas à sessão). */
  invalidateCache(workflowId: string): void {
    this.cache.delete(workflowId);
  }

  async get(
    workflowId: string,
    options?: { force?: boolean }
  ): Promise<WorkflowCardFieldDisplayOrderItem[]> {
    if (!options?.force) {
      const hit = this.cache.get(workflowId);
      if (hit && Date.now() - hit.at < this.ttlMs) {
        return hit.items;
      }
    }
    const raw = await firstValueFrom(
      this.http.get<unknown>(
        `/api/workflows/${encodeURIComponent(workflowId)}/card-field-display-order`
      )
    );
    const items = this.normalizeGet(raw);
    this.cache.set(workflowId, { items, at: Date.now() });
    return items;
  }

  async put(workflowId: string, items: WorkflowCardFieldDisplayOrderPutItem[]): Promise<WorkflowCardFieldDisplayOrderItem[]> {
    const raw = await firstValueFrom(
      this.http.put<unknown>(
        `/api/workflows/${encodeURIComponent(workflowId)}/card-field-display-order`,
        { items }
      )
    );
    const normalized = this.normalizeGet(raw);
    this.cache.set(workflowId, { items: normalized, at: Date.now() });
    return normalized;
  }

  private normalizeGet(response: unknown): WorkflowCardFieldDisplayOrderItem[] {
    const root = response && typeof response === 'object' ? (response as Record<string, unknown>) : {};
    const arr = root['items'] ?? root['Items'];
    if (!Array.isArray(arr)) return [];
    return arr.map((entry) => this.parseItem(entry)).filter((x): x is WorkflowCardFieldDisplayOrderItem => x != null);
  }

  private parseItem(entry: unknown): WorkflowCardFieldDisplayOrderItem | null {
    if (!entry || typeof entry !== 'object') return null;
    const o = entry as Record<string, unknown>;
    const kindRaw = String(o['kind'] ?? o['Kind'] ?? '').trim().toLowerCase();
    const kind: WorkflowCardFieldDisplayOrderKind = kindRaw === 'custom' ? 'custom' : 'native';
    const label = String(o['label'] ?? o['Label'] ?? '').trim() || '—';
    const nativeKey =
      o['nativeCardFieldKey'] != null || o['NativeCardFieldKey'] != null
        ? String(o['nativeCardFieldKey'] ?? o['NativeCardFieldKey']).trim() || null
        : null;
    const wfId =
      o['workflowFieldId'] != null || o['WorkflowFieldId'] != null
        ? String(o['workflowFieldId'] ?? o['WorkflowFieldId']).trim() || null
        : null;
    const locked = !!(o['isTitleLocked'] ?? o['IsTitleLocked']);
    return {
      kind,
      isTitleLocked: locked,
      nativeCardFieldKey: nativeKey,
      workflowFieldId: wfId,
      label,
    };
  }
}
